import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';

import { useToast } from '../components/shared/ToastNotification';

// Premium Customer Form - Modern & High-Impact
function CustomerForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const isEdit = !!id;

    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        gstin: '',
        credit_limit: '',
        billing_address: '',
        city: '',
        state: '',
        pincode: '',
        active: true
    });

    const [loading, setLoading] = useState(isEdit);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isEdit) {
            loadCustomer();
        }
    }, [id]);

    const loadCustomer = async () => {
        try {
            const response = await api.get(`/customers/${id}`);
            if (response.success) {
                const customer = response.data;
                setFormData({
                    name: customer.name || '',
                    phone: customer.phone || '',
                    email: customer.email || '',
                    gstin: customer.gstin || '',
                    credit_limit: customer.credit_limit || '',
                    billing_address: customer.billing_address || '',
                    city: customer.city || '',
                    state: customer.state || '',
                    pincode: customer.pincode || '',
                    active: customer.active !== undefined ? customer.active : true
                });
            } else {
                setError(response.message || 'Failed to retrieve client profile');
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setSaving(true);
            setError(null);

            const response = isEdit
                ? await api.put(`/customers/${id}`, { customer: formData })
                : await api.post('/customers', { customer: formData });

            if (response.success) {
                const responseData = response.data || response;
                toast.success(`Client profile ${isEdit ? 'updated' : 'created'} successfully`);
                navigate(`/customers/${responseData.id}`);
            } else {
                const backendErrors = response.errors?.length > 0
                    ? response.errors
                    : [response.message || response.error || 'Verification error while saving client'];
                
                setError(backendErrors.join(", "));
                toast.error(backendErrors[0]);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (err: any) {
            const msg = err.message || 'An unexpected error occurred';
            setError(msg);
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center">
                <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
                <div className="text-slate-500 font-medium">Fetching profile details...</div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header */}
            <div className="flex items-center justify-between mb-10">
                <div>
                    <h1 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
                        {isEdit ? 'Refine' : 'Add'} <span className="text-indigo-600">Client Profile</span>
                    </h1>
                    <p className="text-slate-500 mt-1 font-medium italic">Store essential contact and billing information</p>
                </div>
                <Link to="/customers" className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </Link>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
                {error && (
                    <div className="bg-rose-50 border-l-4 border-rose-500 p-5 rounded-2xl shadow-sm animate-bounce-short">
                        <div className="flex gap-3 items-center">
                            <svg className="w-5 h-5 text-rose-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <p className="text-sm font-bold text-rose-800">{error}</p>
                        </div>
                    </div>
                )}

                {/* Section 1: Identification */}
                <div className="bg-white rounded-lg border border-slate-100 shadow-xl shadow-slate-200/50 p-8 sm:p-12 transition-all hover:shadow-2xl">
                    <div className="flex items-center gap-3 mb-10">
                        <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-[1.25rem] flex items-center justify-center font-black">1</div>
                        <h2 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em]">Identification basics</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="md:col-span-2 space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.1em] pl-1">Legal Name <span className="text-rose-500">*</span></label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-3xl outline-none focus:bg-white focus:border-indigo-500 selection:bg-indigo-100 transition-all font-black text-slate-900 shadow-sm"
                                placeholder="e.g. Acme Corp or John Doe"
                            />
                        </div>

                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.1em] pl-1">Phone Number</label>
                            <div className="relative">
                                <span className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 font-bold">🇮🇳</span>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className="w-full pl-14 pr-6 py-5 bg-slate-50 border border-slate-100 rounded-3xl outline-none focus:bg-white focus:border-indigo-500 transition-all font-black text-slate-900 shadow-sm"
                                    placeholder="+91 00000 00000"
                                />
                            </div>
                        </div>

                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.1em] pl-1">Professional Email</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-3xl outline-none focus:bg-white focus:border-indigo-500 transition-all font-black text-slate-900 shadow-sm"
                                placeholder="hello@client.com"
                            />
                        </div>
                    </div>
                </div>

                {/* Section 2: Business details */}
                <div className="bg-white rounded-lg border border-slate-100 shadow-xl shadow-slate-200/50 p-8 sm:p-12 transition-all hover:shadow-2xl">
                    <div className="flex items-center gap-3 mb-10">
                        <div className="w-10 h-10 bg-emerald-50 text-emerald-600 rounded-[1.25rem] flex items-center justify-center font-black">2</div>
                        <h2 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em]">Business & Finance</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.1em] pl-1">GST Identification (GSTIN)</label>
                            <input
                                type="text"
                                name="gstin"
                                value={formData.gstin}
                                onChange={handleChange}
                                className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-3xl outline-none focus:bg-white focus:border-indigo-500 transition-all font-black text-slate-900 shadow-sm uppercase placeholder:normal-case"
                                placeholder="22AAAAA0000A1Z5"
                            />
                        </div>

                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.1em] pl-1">Credit Allocation (₹)</label>
                            <input
                                type="number"
                                name="credit_limit"
                                value={formData.credit_limit}
                                onChange={handleChange}
                                step="0.01"
                                min="0"
                                className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-3xl outline-none focus:bg-white focus:border-indigo-500 transition-all font-black text-indigo-600 shadow-sm"
                                placeholder="0.00"
                            />
                        </div>
                    </div>
                </div>

                {/* Section 3: Fulfillment */}
                <div className="bg-white rounded-lg border border-slate-100 shadow-xl shadow-slate-200/50 p-8 sm:p-12 transition-all hover:shadow-2xl">
                    <div className="flex items-center gap-3 mb-10">
                        <div className="w-10 h-10 bg-amber-50 text-amber-600 rounded-[1.25rem] flex items-center justify-center font-black">3</div>
                        <h2 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em]">Address & Status</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="md:col-span-3 space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.1em] pl-1">Billing Location</label>
                            <textarea
                                name="billing_address"
                                value={formData.billing_address}
                                onChange={handleChange}
                                rows={2}
                                className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-3xl outline-none focus:bg-white focus:border-indigo-500 transition-all font-medium text-slate-700 shadow-sm resize-none"
                                placeholder="Suite, Building, Street name..."
                            />
                        </div>

                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.1em] pl-1">City / Hub</label>
                            <input
                                type="text"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:bg-white focus:border-indigo-500 transition-all font-black text-slate-700 shadow-sm"
                            />
                        </div>

                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.1em] pl-1">State / Province</label>
                            <input
                                type="text"
                                name="state"
                                value={formData.state}
                                onChange={handleChange}
                                className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:bg-white focus:border-indigo-500 transition-all font-black text-slate-700 shadow-sm"
                            />
                        </div>

                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.1em] pl-1">Postal Code</label>
                            <input
                                type="text"
                                name="pincode"
                                value={formData.pincode}
                                onChange={handleChange}
                                className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:bg-white focus:border-indigo-500 transition-all font-black text-slate-700 shadow-sm"
                            />
                        </div>

                        <div className="md:col-span-3 pt-4 border-t border-slate-50">
                            <label className="flex items-center gap-4 cursor-pointer group w-fit">
                                <div className="relative">
                                    <input
                                        type="checkbox"
                                        name="active"
                                        checked={formData.active}
                                        onChange={handleChange}
                                        className="sr-only"
                                    />
                                    <div className={`w-14 h-8 rounded-full transition-colors duration-300 ${formData.active ? 'bg-indigo-500' : 'bg-slate-200'}`}></div>
                                    <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform duration-300 shadow-sm ${formData.active ? 'translate-x-6' : ''}`}></div>
                                </div>
                                <div>
                                    <p className="text-sm font-black text-slate-900">Active Account</p>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Allow sales transactions for this client</p>
                                </div>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-6 pb-12">
                    <button
                        type="submit"
                        disabled={saving}
                        className={`flex-1 sm:flex-none px-12 py-5 bg-slate-900 text-white rounded-3xl font-black uppercase tracking-[0.2em] text-xs transition-all shadow-2xl shadow-indigo-200/50 hover:bg-black hover:scale-105 active:scale-95 ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {saving ? 'Processing...' : (isEdit ? 'Update Profile' : 'Save New Profile')}
                    </button>
                    <Link
                        to="/customers"
                        className="px-8 py-5 bg-white border border-slate-200 text-slate-500 rounded-3xl font-black uppercase tracking-[0.2em] text-xs transition-all hover:bg-slate-50 text-center"
                    >
                        Discard
                    </Link>
                </div>
            </form>
        </div>
    );
}

export default CustomerForm;
